import type { InternalPage } from '../lib/pages.ts'

/**
 * Every page in the internal webapp, in the order the rail shows them.
 *
 * This file is the builder lane. It sits under `src/routes/` rather than in
 * `src/lib/` so that adding a page needs no code-owner review: `src/lib/` is
 * owned in full by `.github/CODEOWNERS`, and every page ever added to this
 * repo had to edit the list when it lived there. The shape and the grouping
 * logic stay in `src/lib/pages.ts`, which is framework and stays owned.
 *
 * The `-` prefix is TanStack Router's ignore prefix, so this file adds no
 * route. Add an entry when you add a route under `src/routes/<app>/`, and
 * `-nav.test.ts` fails if you forget.
 *
 * **Every page carries a group, and the group matches the first segment of
 * the path.** A reader navigates `/payments/ach-returns` and looks for it
 * under Payments; a rail that listed it flat would answer a question the URL
 * had already answered differently. `-nav.test.ts` holds the two halves in
 * step, so a page added under a new app folder fails until it names a group.
 *
 * The rail in `__root.tsx` is the only reader. Every signed-in employee sees
 * every entry: per-page access control was removed while no page needed it, so
 * there is no per-viewer answer to ask for.
 */
export const INTERNAL_PAGES: readonly InternalPage[] = Object.freeze([
  {
    id: 'blytzcollect-analytics',
    path: '/blytzcollect/analytics',
    title: 'BlytzCollect Analytics',
    group: 'BlytzCollect'
  },
  {
    id: 'ach-returns',
    path: '/payments/ach-returns',
    title: 'ACH returns',
    group: 'Payments'
  },
  {
    id: 'bin-mcc-fee-report',
    path: '/payments/bin-mcc-fee-report',
    title: 'BIN / MCC fee report',
    group: 'Payments'
  },
  {
    id: 'multicard-usage',
    path: '/payments/multicard-usage',
    title: 'Multicard Usage',
    group: 'Payments'
  },
  {
    id: 'retry-analysis',
    path: '/reports/retry-analysis',
    title: 'Retry Analysis',
    group: 'Reports'
  },
  {
    id: 'ai-texts',
    path: '/reports/ai-texts',
    title: 'AI Texts',
    group: 'Reports'
  },
  {
    id: 'cs-tools-autopay-accounts',
    path: '/cs-tools/autopay-accounts',
    title: 'Autopay Accounts',
    group: 'CS Tools'
  },
  {
    id: 'cs-tools-merchant-notes',
    path: '/cs-tools/merchant-notes',
    title: 'Merchant Notes',
    group: 'CS Tools'
  },
  {
    id: 'cs-tools-registrations',
    path: '/cs-tools/registrations',
    title: 'Registrations',
    group: 'CS Tools'
  }
])
